import * as React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { SiteDetail } from '../../hooks/useSiteDetails';
import { SiteImage } from '../../hooks/useSiteImages';
import { withTranslation } from 'react-i18next';
import SearchSites from './SearchSites';
import SiteInfoClass from './SiteInfo';
import FeedbackMessageView from '../feedbackmessage/FeedbackMessageView';
import Documents from '../homepage/Documents';
import InfoMessages from '../homepage/InfoMessages';
import { LANGUAGE } from '../../constants/storageKeys';
import * as i18next from 'i18next';
import driverinfosecurity from '../../utils/DriverInfoSecurity';
import moment from 'moment';
import { useSiteInfoDetails } from '../../hooks/useSiteDetails';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { isRecentlyUpdated } from '../../constants/recentlyUpdated';


function SiteDetails({ match }: RouteComponentProps<{ siteId: string }>) {
    const siteId = parseInt(match.params.siteId || '0');
    const lang = localStorage[LANGUAGE] || 'sv-SE';

    const [showFeedbackMessage, setShowFeedbackMessage] = React.useState(false);
    const [showSiteDetails, setShowSiteDetails] = React.useState(true);

    React.useEffect(() => {
        setShowSiteDetails(true);
    }, [siteId]);

    const { data: siteInfoDetails } = useSiteInfoDetails(siteId, lang);

    const siteInfo = siteInfoDetails?.siteInfo;
    const filteredSiteDetails = (siteInfoDetails?.siteDetail ?? []).filter(
        (item) => item.value != null && item.value !== ''
    );
    const siteImages = siteInfoDetails?.siteImages ?? [];

    function renderSiteDetail(sd: SiteDetail) {
        if (!sd.isPicture) {
            return (
                <Box key={sd.column} sx={{ display: 'flex', flexDirection: 'column', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight={600}>
                        {i18next.t(sd.column)}:
                    </Typography>
                    <Typography sx={{ wordBreak: 'break-word' }}>{sd.value}</Typography>
                </Box>
            );
        }
        return (
            <Box key={sd.column} sx={{ py: 1 }}>
                {siteImages.map((image) => renderSiteImage(image))}
            </Box>
        );
    }

    function renderSiteImage(image: SiteImage) {
        return (
            <Box key={image.siteImageId} sx={{ mb: 2 }}>
                <img style={{ width: '100%', height: 'auto', display: 'block' }} src={image.imageUrl} alt="" />
            </Box>
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
                <SearchSites
                    onSearchActive={() => setShowSiteDetails(false)}
                    onSearchCleared={() => setShowSiteDetails(true)}
                />

                {showSiteDetails && siteInfo && (
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2, mt: 1 }}>
                        {/* Site header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                                {isRecentlyUpdated(siteInfo.updateDateUTC) && (
                                    <Chip
                                        icon={<FiberNewIcon />}
                                        label={i18next.t('site.recentlyUpdated')}
                                        data-testid="recently-updated"
                                        color="warning"
                                        size="small"
                                        sx={{ mb: 1, fontWeight: 600 }}
                                    />
                                )}
                                <SiteInfoClass
                                    key={siteInfo.uniqueSiteId + siteInfo.siteName}
                                    site={siteInfo}
                                    fromAdmin={false}
                                    includeState={false}
                                />
                            </Box>
                            <Box className="siteSettings" sx={{ display: 'flex', flexShrink: 0, ml: 2 }}>
                                {driverinfosecurity.isAdmin() && (
                                    <IconButton
                                        component={Link}
                                        to={'/administrationsiteinfo/' + siteInfo.uniqueSiteId}
                                        size="small"
                                        color="primary"
                                    >
                                        <SettingsIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => setShowFeedbackMessage(true)}
                                >
                                    <FeedbackIcon fontSize="small" />
                                </IconButton>
                                {showFeedbackMessage && (
                                    <FeedbackMessageView
                                        siteId={siteInfo.uniqueSiteId}
                                        handleClose={() => setShowFeedbackMessage(false)}
                                        show={showFeedbackMessage}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Brand */}
                        <Divider sx={{ my: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img src={new URL('../../img/brands.png', import.meta.url).href} alt="" />
                                <Typography fontWeight={600}>{i18next.t('site.brandLabel')}</Typography>
                            </Box>
                        </Divider>
                        <Typography sx={{ mb: 1 }}>{siteInfo.brand}</Typography>

                        {/* Contacts */}
                        <Divider sx={{ my: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img src={new URL('../../img/info-contact.png', import.meta.url).href} alt="" />
                                <Typography fontWeight={600}>{i18next.t('site.contactsLabel')}</Typography>
                            </Box>
                        </Divider>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                {siteInfo.telephoneExchange && (
                                    <Typography sx={{ mb: 2 }}>
                                        <strong>{i18next.t('site.TelephoneExchange')}:</strong> {siteInfo.telephoneExchange}
                                    </Typography>
                                )}
                                {siteInfo.telephoneAlternative && (
                                    <Typography sx={{ mb: 2 }}>
                                        <strong>{i18next.t('site.TelephoneAlternative')}:</strong> {siteInfo.telephoneAlternative}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                {siteInfo.contact && (
                                    <Typography>
                                        <strong>{i18next.t('site.Contact')}:</strong> {siteInfo.contact}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        {/* Instructions */}
                        <Divider sx={{ my: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img src={new URL('../../img/Instructions.png', import.meta.url).href} alt="" />
                                <Typography fontWeight={600}>{i18next.t('site.instructionsLabel')}</Typography>
                            </Box>
                        </Divider>
                        {filteredSiteDetails.map(sd => renderSiteDetail(sd))}

                        {/* Last updated */}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">{i18next.t('common.lastupdated')}:</Typography>
                            <Typography variant="body2">
                                {siteInfo.updateDateUTC ? moment.utc(siteInfo.updateDateUTC).local().format('L HH:mm') : ''}
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <InfoMessages />
                <Box sx={{ mt: 9 }}>
                    <Documents />
                </Box>
            </Grid>
        </Grid>
    );
}

export default withTranslation('translations')(SiteDetails);
